import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { TemplateFrame } from '../../site/TemplateFrame';
import { useAuth } from '../../lib/useAuth';
import { Field, FormNotice } from './MemberForm';
import { MemberBookingsCard } from './MemberBookingsCard';
import { MemberPasswordCard } from './MemberPasswordCard';
import { fieldErrorsFrom, messageFrom, type FieldErrors } from './memberErrors';

const MAX_BIO = 2000;

export function MemberAccountPage() {
  const { member, status, updateMember, uploadAvatar, logout } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    bio: '',
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  // Seed the form from the profile once it arrives, and whenever it changes
  // underneath us (an avatar upload returns a fresh profile).
  useEffect(() => {
    if (!member) return;
    setForm({
      firstName: member.firstName ?? '',
      lastName: member.lastName ?? '',
      email: member.email,
      username: member.username,
      bio: member.bio ?? '',
    });
  }, [member]);

  if (status === 'loading') {
    return (
      <TemplateFrame title="My Account">
        <p className="po-muted text-center">Loading your account…</p>
      </TemplateFrame>
    );
  }
  if (status !== 'authenticated' || !member) return <Navigate to="/login" replace />;

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSaved(false);
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setErrors({});
    setSaved(false);
    setBusy(true);
    try {
      await updateMember(form);
      setSaved(true);
    } catch (err) {
      setErrors(fieldErrorsFrom(err));
      setError(messageFrom(err, 'We could not save those changes.'));
    } finally {
      setBusy(false);
    }
  };

  const pickAvatar = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setAvatarError(null);
    setAvatarBusy(true);
    try {
      await uploadAvatar(file);
    } catch (err) {
      // The upstream classifies every image: pornography is refused outright,
      // and a profile photo may not contain nudity.
      setAvatarError(messageFrom(err, 'We could not upload that image.'));
    } finally {
      setAvatarBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const initials =
    `${member.firstName?.[0] ?? ''}${member.lastName?.[0] ?? ''}`.trim() ||
    member.username.slice(0, 2).toUpperCase();

  return (
    <TemplateFrame title="My Account">
      <div className="row row-50 justify-content-center">
        <div className="col-md-10 col-lg-4">
          <div className="member-card text-center">
            <div className="member-avatar">
              {member.avatarUrl ? (
                <img src={member.avatarUrl} alt={`${member.username}'s avatar`} />
              ) : (
                <span className="member-avatar-initials">{initials}</span>
              )}
            </div>

            <h5>{member.username}</h5>
            <p className="po-muted">{member.email}</p>

            {member.avatarPending ? (
              <FormNotice kind="info">
                Your photo has been uploaded and is waiting to be approved. It will appear here once
                it is.
              </FormNotice>
            ) : null}
            {avatarError ? <FormNotice kind="error">{avatarError}</FormNotice> : null}

            <input
              ref={fileRef}
              id="avatar"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="member-file"
              onChange={pickAvatar}
            />
            <label className="button button-primary button-block" htmlFor="avatar">
              {avatarBusy ? 'Uploading…' : member.avatarUrl ? 'Change photo' : 'Upload a photo'}
            </label>
            <p className="member-hint">JPEG, PNG or WebP. Profile photos cannot contain nudity.</p>

            <button
              type="button"
              className="button button-default-outline button-block"
              onClick={() => void logout()}
            >
              Sign out
            </button>
          </div>
        </div>

        <div className="col-md-10 col-lg-8">
          {/* Above the profile form on purpose: somebody who opens their account
              after booking has come to check the stay, not to edit their bio. */}
          <MemberBookingsCard />

          <div className="member-card">
            <h3>Your details</h3>

            {error ? <FormNotice kind="error">{error}</FormNotice> : null}
            {saved ? <FormNotice kind="info">Your details have been saved.</FormNotice> : null}

            <form onSubmit={submit} noValidate>
              <div className="row row-17">
                <div className="col-sm-6">
                  <Field label="First name" name="first_name" errors={errors}>
                    <input
                      id="first_name"
                      className="form-input"
                      value={form.firstName}
                      onChange={set('firstName')}
                    />
                  </Field>
                </div>
                <div className="col-sm-6">
                  <Field label="Last name" name="last_name" errors={errors}>
                    <input
                      id="last_name"
                      className="form-input"
                      value={form.lastName}
                      onChange={set('lastName')}
                    />
                  </Field>
                </div>
              </div>

              <Field label="Email" name="email" errors={errors}>
                <input
                  id="email"
                  className="form-input"
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                />
              </Field>

              <Field label="Username" name="username" errors={errors}>
                <input
                  id="username"
                  className="form-input"
                  value={form.username}
                  onChange={set('username')}
                />
              </Field>

              <Field
                label="Bio"
                name="bio"
                errors={errors}
                hint={`${form.bio.length} of ${MAX_BIO} characters`}
              >
                <textarea
                  id="bio"
                  className="form-input"
                  rows={5}
                  maxLength={MAX_BIO}
                  value={form.bio}
                  onChange={set('bio')}
                />
              </Field>

              <button className="button button-primary" type="submit" disabled={busy}>
                {busy ? 'Saving…' : 'Save changes'}
              </button>
            </form>
          </div>

          <MemberPasswordCard />
        </div>
      </div>
    </TemplateFrame>
  );
}
